import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FaCheck,
  FaChevronDown,
  FaTimes,
} from "react-icons/fa";

const getDefaultOptionLabel = (option) => option;

const MultiCheckFilter = ({
  label,
  options = [],
  value = [],
  onChange,
  getOptionLabel = getDefaultOptionLabel,
  placeholder = "Toutes",
}) => {
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const menuId = useId();

  const selected = Array.isArray(value) ? value : [];

  const selectedKeys = useMemo(
    () => new Set(selected.map(String)),
    [selected]
  );

  const selectedLabel = useMemo(() => {
    if (!selected.length) return placeholder;

    if (selected.length === 1) {
      return getOptionLabel(selected[0]);
    }

    return `${selected.length} sélectionnées`;
  }, [selected, placeholder, getOptionLabel]);

  // =====================================================
  // Fermeture au clic extérieur et touche Échap
  // =====================================================

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // =====================================================
  // Fermer les autres MultiCheckFilter
  // =====================================================

  useEffect(() => {
    const handleOtherFilterOpen = (event) => {
      if (event.detail?.id !== menuId) {
        setOpen(false);
      }
    };

    window.addEventListener(
      "multicheckfilter:open",
      handleOtherFilterOpen
    );

    return () => {
      window.removeEventListener(
        "multicheckfilter:open",
        handleOtherFilterOpen
      );
    };
  }, [menuId]);

  const toggleMenu = () => {
    if (!open) {
      window.dispatchEvent(
        new CustomEvent("multicheckfilter:open", {
          detail: { id: menuId },
        })
      );
    }

    setOpen((current) => !current);
  };

  // =====================================================
  // Sélection des valeurs
  // =====================================================

  const toggleValue = (option) => {
    const key = String(option);

    if (selectedKeys.has(key)) {
      onChange?.(
        selected.filter((item) => String(item) !== key)
      );
      return;
    }

    onChange?.([...selected, option]);
  };

  const clear = () => {
    onChange?.([]);
  };

  const closeMenu = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className="relative min-w-0"
    >
      {/* ============================================= */}
      {/* Bouton principal */}
      {/* ============================================= */}

      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${label} : ${selectedLabel}`}
        className="
          flex min-h-11 w-full min-w-0 items-center
          justify-between gap-2 rounded-xl
          border border-slate-200 bg-white
          px-3 py-2 text-left text-sm
          text-slate-800 shadow-sm transition
          hover:border-orange-300
          focus:outline-none
          focus-visible:ring-2
          focus-visible:ring-orange-400
          dark:border-white/10
          dark:bg-slate-900
          dark:text-white
        "
      >
        <span className="min-w-0">
          <span className="
            block text-[10px] font-bold uppercase
            tracking-[0.16em] text-slate-400
          ">
            {label}
          </span>

          <span className="block truncate font-semibold">
            {selectedLabel}
          </span>
        </span>

        <FaChevronDown
          className={`
            h-3 w-3 shrink-0 text-slate-400
            transition-transform duration-200
            ${open ? "rotate-180" : ""}
          `}
        />
      </button>

      {/* ============================================= */}
      {/* Menu déroulant */}
      {/* ============================================= */}

      {open && (
        <div
          id={menuId}
          className="
            absolute left-0 right-0 top-full
            z-50 mt-2
            flex max-h-80 flex-col
            overflow-hidden rounded-2xl
            border border-slate-200
            bg-white shadow-2xl
            dark:border-white/10
            dark:bg-slate-950
          "
        >
          {/* En-tête fixe */}

          <div className="
            flex shrink-0 items-center justify-between
            gap-2 border-b border-slate-100
            px-3 py-2
            dark:border-white/10
          ">
            <div className="min-w-0">
              <p className="
                text-xs font-bold
                text-slate-700
                dark:text-slate-200
              ">
                {label}
              </p>

              <p className="
                text-[11px] text-slate-400
              ">
                {selected.length} sélection
                {selected.length > 1 ? "s" : ""}
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={clear}
                disabled={!selected.length}
                className="
                  inline-flex items-center gap-1
                  rounded-lg px-2 py-1.5
                  text-xs font-bold text-orange-600
                  transition hover:bg-orange-50
                  disabled:cursor-not-allowed
                  disabled:opacity-40
                  dark:text-orange-300
                  dark:hover:bg-orange-500/10
                "
              >
                <FaTimes className="h-3 w-3" />
                Vider
              </button>

              <button
                type="button"
                onClick={closeMenu}
                aria-label={`Fermer le filtre ${label}`}
                className="
                  flex h-7 w-7 items-center
                  justify-center rounded-lg
                  text-slate-500 transition
                  hover:bg-slate-100
                  hover:text-slate-900
                  dark:text-slate-300
                  dark:hover:bg-white/10
                  dark:hover:text-white
                "
              >
                <FaTimes className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Liste avec défilement */}

          <div className="
            min-h-0 flex-1 overflow-y-auto
            overscroll-contain p-2
          ">
            {options.length ? (
              <div className="grid gap-1">
                {options.map((option) => {
                  const checked = selectedKeys.has(
                    String(option)
                  );

                  return (
                    <label
                      key={String(option)}
                      className={`
                        flex cursor-pointer items-center
                        gap-2 rounded-xl px-2 py-2
                        text-sm transition
                        ${
                          checked
                            ? "bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200"
                            : "hover:bg-slate-50 dark:hover:bg-white/5"
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          toggleValue(option)
                        }
                        className="
                          h-4 w-4 shrink-0 rounded
                          border-slate-300
                          text-orange-600
                          focus:ring-orange-500
                        "
                      />

                      <span className="
                        min-w-0 truncate font-semibold
                      ">
                        {getOptionLabel(option)}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="
                px-2 py-6 text-center text-sm
                text-slate-500
                dark:text-slate-400
              ">
                Aucune option disponible.
              </div>
            )}
          </div>

          {/* Pied de menu fixe */}

          <div className="
            shrink-0 border-t border-slate-100
            bg-slate-50 px-3 py-2
            dark:border-white/10
            dark:bg-slate-900
          ">
            <button
              type="button"
              onClick={closeMenu}
              className="
                flex min-h-9 w-full items-center
                justify-center gap-2 rounded-xl
                bg-orange-500 px-3 py-2
                text-xs font-bold text-white
                shadow-sm transition
                hover:bg-orange-600
                focus:outline-none
                focus-visible:ring-2
                focus-visible:ring-orange-400
              "
            >
              <FaCheck className="h-3 w-3" />
              Terminer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiCheckFilter;